import { Loading } from "components/shared";
import { Button, Tabs, Dialog } from "components/ui";
import React, { useEffect, useRef, useState } from "react";
import { formatNumber } from "utils/formatNumber";
import TabContent from "components/ui/Tabs/TabContent";
import TabList from "components/ui/Tabs/TabList";
import TabNav from "components/ui/Tabs/TabNav";
import ExploreMedia from "views/media/ExploreMedia"
import { TbX } from "react-icons/tb";

const ThumbnailPicker = ({
    onSelectVideoCoverTimestamp,
    onSelectCoverImage,
    post,
    showFrameTab = true,
    showFileTab = false,
    setPreventClose = () => {}
}) => {

    const [ videoDurationMs, setVideoDurationMs ] = useState();
    const [ newTimestamp, setNewTimestamp ] = useState(post?.preferences?.video_cover_timestamp_ms)
    const width = 250;
    const videoRef = useRef();
    const draggerRef = useRef();
    const [ selectAssetModalOpened, setSelectAssetModalOpened ] = useState(false);

    const defaultTab = post?.cover?.url ? 'upload' : 'frame';

    const updateVideoCoverTimestampMs = async (_timestamp = newTimestamp) => {
        onSelectVideoCoverTimestamp(_timestamp);
        setNewTimestamp(_timestamp);
        
        // Force close the parent dropdown
        setPreventClose(false);
        await new Promise(resolve => setTimeout(resolve, 100));
        const dropdownToggle = document.activeElement;
        dropdownToggle?.click();
    }

    const dragX = (evt) => {
        const el = evt.currentTarget;
        const move = (evt) => {
            if (el.offsetLeft + evt.movementX < 0 || el.offsetLeft + evt.movementX > width) return;
            const newPosition = el.offsetLeft + evt.movementX;
            el.style.left = `${newPosition}px`;
            if (videoRef.current) {
                const timestamp = parseInt((newPosition / width) * videoDurationMs);
                videoRef.current.currentTime = timestamp / 1000;
                setNewTimestamp(timestamp);
            }
        };
        const up = () => {
            window.removeEventListener("pointermove", move);
            window.removeEventListener("pointerup", up);
        };
        window.addEventListener("pointermove", move);
        window.addEventListener("pointerup", up);
    };
    
    useEffect(() => {
        if (videoDurationMs && draggerRef.current && post?.preferences?.video_cover_timestamp_ms) {
            const newPosition = post?.preferences?.video_cover_timestamp_ms / videoDurationMs * width;
            draggerRef.current.style.left = `${newPosition}px`;
            videoRef.current.currentTime = post?.preferences?.video_cover_timestamp_ms;
        }
    }, [videoDurationMs, draggerRef.current,]);

    useEffect(() => {
        if (post?.preferences?.video_cover_timestamp_ms > videoDurationMs && draggerRef.current) {
            updateVideoCoverTimestampMs(videoDurationMs);
            draggerRef.current.style.left = `${width}px`;
        }
    }, [videoDurationMs, post?.preferences?.video_cover_timestamp_ms, draggerRef.current]);

    const updateCoverImage = async (imageURL) => {
        setSelectAssetModalOpened(false);
        onSelectCoverImage(imageURL);
        
        // Force close the parent dropdown
        setPreventClose(false);
        await new Promise(resolve => setTimeout(resolve, 100));
        const dropdownToggle = document.activeElement;
        dropdownToggle?.click();
    }

    if (!showFrameTab && !showFileTab) {
        return null;
    }

    return (
        <div style={{ width: `${width}px` }} className={`shrink-0 text-tertiary-400 dark:text-gray-700 flex flex-col items-center border border-gray-200 dark:border-gray-700 !h-auto`}>
            <Tabs defaultValue={defaultTab} variant="pill" className="w-full">
                <TabList className="w-full border-b border-tertiary-100 dark:border-gray-700 border-solid">
                    {showFrameTab && (
                        <TabNav value="frame" className="flex-grow grow flex-1 !text-sm m-0 rounded-none py-[13px] flex items-center justify-center gap-1 px-0">
                            <span className='!text-[12px]'>Choose a frame</span>
                        </TabNav>
                    )}
                    {showFileTab && (
                        <TabNav value="upload" className="flex-grow grow flex-1 !text-sm m-0 rounded-none py-[13px] flex items-center justify-center gap-1 px-0">
                            <span className='!text-[12px]'>Upload cover</span>
                        </TabNav>
                    )}
                </TabList>
                <div className="p-4">
                    <TabContent value="frame" className="gap-4 flex flex-col">
                        <div className='relative flex'>
                            <Loading type={`element`} className="absolute left-1/2 top-1/2 -translate-y-1/2 -translate-x-1/2" />
                            <video ref={videoRef} preload="auto" onDurationChange={(e) => setVideoDurationMs(e.target.duration * 1000)} controlsList="track" src={`${post?.assets?.[0]?.video?.url}`} autoPlay={false} controls={false} className={`relative z-[2] object-cover aspect-[9/14] bg-gray-200`} />
                        </div>
                        <div className='relative w-full h-1.5 bg-primary-600/30'>
                            <span ref={draggerRef} onPointerDown={dragX} className='cursor-grab active:cursor-grabbing z-[3] mt-[3px] inline-block rounded-full h-6 w-6 absolute left-0 top-0 -translate-y-1/2 -translate-x-1/2 bg-primary-600 shadow-lg'></span>
                        </div>
                        <div className="flex items-center justify-center gap-2 py-2">
                            <span className='text-xs text-center'>Video cover</span>
                            <span className='text-base text-center text-tertiary-800 font-semibold'>{formatNumber((newTimestamp || 0) / 1000, 2)}s</span>
                        </div>
                        <Button
                            variant="twoTone"
                            className="!rounded-none w-full"
                            onClick={() => updateVideoCoverTimestampMs()}
                        >
                            Update Video Cover
                        </Button>
                    </TabContent>
                    <TabContent value="upload" className="gap-4 flex flex-col">
                        <div className="w-full">
                            <div className="relative flex flex-col items-center justify-center aspect-[9/14] bg-tertiary-100 dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                                {(post?.cover?.url) ? (
                                    <div className="relative w-full h-full">
                                        <img
                                            onClick={() => {
                                                setSelectAssetModalOpened(true);
                                                setPreventClose(true);
                                            }}
                                            src={post?.cover?.url}
                                            alt="Preview"
                                            className="w-full h-full object-cover absolute inset-0 cursor"
                                        />
                                        <div 
                                            onClick={() => {
                                                onSelectCoverImage(null);
                                            }}
                                            className="absolute w-6 h-6 top-2.5 right-2.5 rounded-full bg-gray-200 dark:bg-gray-700 p-1.5 flex items-center justify-center cursor-pointer"
                                        >
                                            <TbX className="text-tertiary-500 dark:text-gray-400 dark:text-gray-400" />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center p-4 w-full h-full flex flex-col items-center justify-center cursor-pointer" onClick={() => {
                                        setSelectAssetModalOpened(true);
                                        setPreventClose(true);
                                    }}>
                                        <svg className="mx-auto h-12 w-12 text-tertiary-400" stroke="currentColor" fill="var(--color-tertiary-500)" viewBox="0 0 48 48">
                                            <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                        <p className="mt-2 text-sm font-medium text-tertiary-700 dark:text-gray-300">
                                            Choose a cover image
                                        </p>
                                        <p className="mt-1 text-xs text-tertiary-500 dark:text-gray-400 dark:text-gray-400">
                                            PNG, JPG, GIF max 10MB
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </TabContent>
                </div>
            </Tabs>
            <Dialog 
                isOpen={selectAssetModalOpened} 
                onClose={() => setSelectAssetModalOpened(false)} 
                onRequestClose={() => setSelectAssetModalOpened(false)} 
                overlayClassName="flex items-center justify-center" 
                portalClassName="!z-[100000000] relative"
                width={1200} 
                closable={true}
            >
                <ExploreMedia 
                    allowedTypes={['image']} 
                    isSelectable={true} 
                    isMultipleSelect={false}
                    showMyAssets={true} 
                    initialSelectedAssets={
                        post?.cover?.url 
                            ? [{
                                originalSrc: post?.cover?.url,
                                asset: {
                                    image: {
                                        url: post?.cover?.url
                                    }
                                }
                            }] 
                            : []
                    }
                    handleOnUnselect={(original, asset) => {
                        onSelectCoverImage(null);
                        return true;
                    }}
                    handleOnSelect={(original, asset) => {
                        updateCoverImage(original);

                        return true;
                    }}
                />
            </Dialog>
        </div>
    )
}

export default ThumbnailPicker;